<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { ElMessage } from 'element-plus';
import { useAuthStore } from '@/stores/auth.store';
import { useResourceSummaryStore } from '@/stores/resourceSummary.store';
import { useSessionStore } from '@/stores/session.store';
import ResourceChip from './ResourceChip.vue';
import PlacesChip from './PlacesChip.vue';
import HeaderGlobalNav from './HeaderGlobalNav.vue';
import type { DashboardResource } from '@/types/planetDashboard';
import { assetUrl } from '@/utils/assetUrl';

// URL flouz : on passe par assetUrl pour eviter que Vite/Rollup tente de
// resoudre le chemin absolu '/images/resources/flouz.png' comme un import
// au build (cf erreur CI). Le fichier reste dans public/ et est servi tel quel.
const flouzImgUrl = assetUrl('resources', 'flouz');

const auth = useAuthStore();
const resourceSummary = useResourceSummaryStore();
const session = useSessionStore();

// La race du joueur (icône + nom + jauges +/−) est désormais affichée dans le
// header de planète via <RaceBadge> (cf PlanetHomeView), plus dans ce menu.

// Marqueur de version visible : confirme la build front réellement servie
// (utile pour vérifier qu'un déploiement a bien pris). commit = CI_COMMIT_SHORT_SHA.
const buildVersion = `v${__APP_VERSION__}·${__BUILD_COMMIT__}`;
const buildDate = __BUILD_DATE__;
const router = useRouter();

/**
 * Refonte UX 2026-05-20 : regroupe les ressources par USAGE plutot que par
 * la categorie technique EResourceCategory. Les codes sont stables (seeds
 * ResourceEntityConfiguration) -> mapping fait cote front pour eviter un
 * deploy backend. Un code non mappe tombe dans 'consommables' (fallback).
 *
 *   - materiaux    : minerais de construction (fer, bois, cuivre, ...)
 *   - consommables : carburants + polymeres (deuterium, petrole, plastique)
 *   - precieuses   : gemmes + matiere exotique (saphir, diamant, ..., element_z)
 *   - vie          : flux planetaire (habitants + nourriture + energie)
 *
 * Toutes les ressources du catalogue remontent meme avec stock 0 (LEFT
 * JOIN cote MeService) pour eviter qu'une planete pre-mine ait l'air vide.
 */
type ResourceGroupKey = 'materiaux' | 'consommables' | 'precieuses' | 'vie';

const RESOURCE_GROUP_OF: Record<string, ResourceGroupKey> = {
  fer: 'materiaux',
  bois: 'materiaux',
  cuivre: 'materiaux',
  pierre: 'materiaux',
  aluminium: 'materiaux',
  bauxite: 'materiaux',
  ceramique: 'materiaux',
  plastique: 'materiaux',
  deuterium: 'consommables',
  petrole: 'consommables',
  uranium: 'consommables',
  saphir: 'precieuses',
  emeraude: 'precieuses',
  diamant: 'precieuses',
  rubis: 'precieuses',
  topas: 'precieuses',
  element_z: 'precieuses',
  habitants: 'vie',
  nourriture: 'vie',
  energie: 'vie',
};

// Regroupement piloté par l'axe EResourceOrigin (rôle économique) renvoyé par
// le backend. On garde les 4 cellules fixes de la grille 2x2 du header, mais
// leur contenu/libellé suit désormais l'origine. flouz (Devise) n'apparaît pas
// dans la grille (c'est le wallet, affiché à part).
const GROUP_LABEL: Record<ResourceGroupKey, string> = {
  materiaux: 'Matières premières',
  consommables: 'Fabriquées',
  precieuses: 'Précieuses',
  vie: 'Vie & énergie',
};

// Origine -> cellule de la grille. Primaire = matières brutes, Fabriquée =
// raffinées (centrales), Précieuse = gemmes/exotique, Vitale+Énergie = vie.
const ORIGIN_TO_CELL: Record<string, ResourceGroupKey> = {
  Primaire: 'materiaux',
  Fabriquee: 'consommables',
  Precieuse: 'precieuses',
  Vitale: 'vie',
  Energie: 'vie',
  Devise: 'vie',
};

const ORDER: ResourceGroupKey[] = ['materiaux', 'consommables', 'precieuses', 'vie'];

function resourceCell(r: DashboardResource): ResourceGroupKey {
  // Priorité à l'origine (data-driven) ; fallback sur l'ancien mapping par
  // code pour les anciens payloads qui ne portent pas encore origin.
  if (r.origin) return ORIGIN_TO_CELL[r.origin] ?? 'consommables';
  return RESOURCE_GROUP_OF[r.code] ?? 'consommables';
}

const resourceGroups = computed<{ key: ResourceGroupKey; label: string; resources: DashboardResource[] }[]>(() => {
  const byGroup = new Map<ResourceGroupKey, DashboardResource[]>();
  for (const r of resourceSummary.resources) {
    const g = resourceCell(r);
    const arr = byGroup.get(g) ?? [];
    arr.push(r);
    byGroup.set(g, arr);
  }
  // Pas de filtre sur les groupes vides : on garde les 4 groupes en positions
  // FIXES dans la grille 2x2 (Materiaux/Consommables/Precieuses/Vie). En
  // pratique tous remontent (LEFT JOIN MeService, stock 0 inclus).
  return ORDER
    .map(g => ({ key: g, label: GROUP_LABEL[g], resources: byGroup.get(g) ?? [] }));
});

// Acces direct aux ressources d'un groupe (pour placer chaque groupe dans sa
// propre cellule de la grille 2x3 du header).
const groupMap = computed<Record<ResourceGroupKey, DashboardResource[]>>(() => {
  const m = { materiaux: [], consommables: [], precieuses: [], vie: [] } as Record<ResourceGroupKey, DashboardResource[]>;
  for (const g of resourceGroups.value) m[g.key] = g.resources;
  return m;
});

// Mobile : drawer plein-ecran pour le bar de ressources (cache en desktop)
const mobileResourcesOpen = ref(false);

async function onLogout(): Promise<void> {
  await auth.logout();
  session.clear();
  resourceSummary.clear();
  ElMessage.success('Deconnecte.');
  await router.push({ name: 'login' });
}

/**
 * Quitte l'univers actif sans déconnecter le compte. Le joueur retourne
 * sur la page d'accueil (HomeView) qui ré-affichera le picker d'univers.
 * Le lobby (re)devient accessible depuis là.
 */
async function onLeaveUniverse(): Promise<void> {
  // session.clear() AVANT la navigation : `inUniverse` repasse à false, donc le
  // guard router autorise l'accès au lobby (sinon il rebondirait vers la home).
  session.clear();
  resourceSummary.clear();
  ElMessage.info('Tu as quitté l\'univers — tes parties restent disponibles.');
  // Retour direct au lobby de sélection d'univers (et non la home qui ne faisait
  // qu'y rebondir).
  await router.push({ name: 'lobby' });
}

function formatNumber(value: number): string {
  if (Math.abs(value) >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (Math.abs(value) >= 1_000) return `${(value / 1_000).toFixed(1)}k`;
  return String(Math.round(value));
}

/**
 * Valeur affichée dans le chip selon le COMPORTEMENT de la ressource :
 * le Flux (énergie) n'a pas de stock (Available = 0 par design) -> on affiche
 * le solde net /h (prod - conso). Les autres comportements (Consommable /
 * Population / Devise) affichent leur disponible/solde courant.
 */
function resourceHeadline(r: DashboardResource): string {
  if ((r.category ?? 'Consommable') === 'Flux') {
    const v = r.productionPerHour;
    return `${v >= 0 ? '+' : ''}${formatNumber(v)}/h`;
  }
  return formatNumber(r.available);
}
</script>

<template>
  <header class="app-header">
    <!-- Bloc gauche : identite + contexte joueur. Adaptatif :
         - Wide (>= $bp-xl) : brand + contexte sur une seule ligne
         - Tablet/desktop etroit (768 - 1279px) : empile -- brand en haut,
           contexte (flouz + planete) en dessous. Aligne le bord gauche
           des chips ressources sur les 2 lignes.
         - Mobile (<768px) : brand cache, le drawer ressources prend le relai. -->
    <div class="app-header-left">
      <router-link to="/" class="app-header-brand" :title="'The H\'O — The Harmonic Orbital'">
        <span class="app-header-brand-text">The H'O</span>
        <span class="app-header-version" :title="`Build ${buildDate}`">{{ buildVersion }}</span>
      </router-link>

      <!-- Contexte joueur (flouz solde + planete courante) sorti du flux
           des chips ressources : ainsi les chips demarrent toujours au
           meme X sur les 2 lignes quand ca wrap.
           Le chip flouz affiche le PORTEFEUILLE (utilisable). Le tooltip
           detaille banque + prets si presents. -->
      <div v-if="session.inUniverse" class="app-header-context">
        <AppInfoCard placement="bottom" :width="320">
            <div class="app-header-resource app-header-flouz">
              <img
                :src="flouzImgUrl"
                alt="Flouz"
                class="app-header-resource-icon"
                loading="lazy"
                v-img-hide-on-error
              />
              <span class="app-header-resource-amount text-mono">
                {{ formatNumber(resourceSummary.playerFlouz) }}
              </span>
            </div>
          <template #content>
          <div class="tt-card flouz-tip">
            <div class="flouz-tip-head">
              <img
                :src="flouzImgUrl"
                alt="Flouz"
                class="flouz-tip-icon"
                v-img-hide-on-error
              />
              <div>
                <div class="flouz-tip-name">Patrimoine du joueur</div>
                <div class="flouz-tip-sub text-muted text-xs">
                  Seul le portefeuille est utilisable pour construire/acheter.
                </div>
              </div>
            </div>
            <!-- Lignes : Portefeuille (utilisable) + Banque (dort) + Total -->
            <div class="flouz-tip-row">
              <span class="flouz-tip-label">Portefeuille</span>
              <span class="flouz-tip-val text-mono">
                {{ resourceSummary.playerFlouz.toLocaleString() }}
              </span>
            </div>
            <div class="flouz-tip-row flouz-tip-bank">
              <span class="flouz-tip-label">
                <el-icon><Lock /></el-icon> Banque
              </span>
              <span class="flouz-tip-val text-mono">
                {{ resourceSummary.bankDeposits.toLocaleString() }}
              </span>
            </div>
            <div class="flouz-tip-row flouz-tip-total">
              <span class="flouz-tip-label">Total</span>
              <span class="flouz-tip-val text-mono">
                {{ (resourceSummary.playerFlouz + resourceSummary.bankDeposits).toLocaleString() }}
              </span>
            </div>

            <!-- Liste des prets actifs (collapse implicite : un par ligne).
                 Cache le bloc entier s'il n'y a aucun pret pour ne pas
                 polluer le tooltip des joueurs sans dette. -->
            <template v-if="resourceSummary.activeLoans.length > 0">
              <div class="flouz-tip-sep" />
              <div class="flouz-tip-section text-muted text-xs">
                Prêts actifs ({{ resourceSummary.activeLoans.length }})
              </div>
              <div
                v-for="l in resourceSummary.activeLoans"
                :key="l.loanId"
                class="flouz-tip-loan"
              >
                <div class="flouz-tip-loan-head">
                  <span class="flouz-tip-loan-name text-xs">
                    {{ l.originPlanetName ?? `Planète #${l.originPlanetId}` }}
                  </span>
                  <span class="flouz-tip-loan-rate text-mono text-xs">
                    {{ Number(l.hourlyInterestPct).toFixed(2) }}%/h
                  </span>
                </div>
                <div class="flouz-tip-loan-row">
                  <span class="flouz-tip-label">Principal dû</span>
                  <span class="text-mono">{{ l.remainingPrincipal.toLocaleString() }} / {{ l.principal.toLocaleString() }}</span>
                </div>
                <div class="flouz-tip-loan-row flouz-tip-loan-warn" v-if="l.accruedInterest > 0">
                  <span class="flouz-tip-label">Intérêts dus</span>
                  <span class="text-mono">{{ l.accruedInterest.toLocaleString() }}</span>
                </div>
                <div class="flouz-tip-loan-row flouz-tip-loan-paid" v-if="l.interestRepaid > 0">
                  <span class="flouz-tip-label">Intérêts remboursés</span>
                  <span class="text-mono">{{ l.interestRepaid.toLocaleString() }}</span>
                </div>
              </div>
            </template>
          </div>
          </template>
        </AppInfoCard>

        <router-link
          v-if="resourceSummary.planetName && resourceSummary.planetId"
          :to="{ name: 'planet', params: { id: resourceSummary.planetId } }"
          class="app-header-context-planet app-header-context-planet-link"
          :title="`Revenir sur ${resourceSummary.planetName}`"
        >
          <el-icon><HomeFilled /></el-icon>
          <span class="app-header-context-planet-name">{{ resourceSummary.planetName }}</span>
        </router-link>
        <AppTooltip
          v-else-if="resourceSummary.planetName"
          :content="resourceSummary.planetName"
        >
          <span class="app-header-context-planet text-muted">
            {{ resourceSummary.planetName }}
          </span>
        </AppTooltip>
      </div>
    </div>

    <!-- Cellules ressources de la grille 2x3 (desktop) :
         1:2 = construction (materiaux) | 1:3 = carburants (consommables)
         2:2 = precieuses               | 2:3 = vie (energie/habitants/nourriture)
         Cachees sous 768px (le drawer mobile prend le relai). -->
    <template v-if="session.inUniverse">
      <!-- Ligne 1 : construction | carburants (separateur entre les 2 types) -->
      <div class="ah-res-row ah-res-top" role="status" aria-live="polite">
        <ResourceChip v-for="r in groupMap.materiaux" :key="r.resourceId" :resource="r" group="materiaux" fluid />
        <span v-if="groupMap.materiaux.length && groupMap.consommables.length" class="ah-res-sep" aria-hidden="true" />
        <ResourceChip v-for="r in groupMap.consommables" :key="r.resourceId" :resource="r" group="consommables" fluid />
      </div>
      <!-- Ligne 2 : precieuses | special/vie (separateur entre les 2 types).
           Le chip "Places" (emplacements de batiments) complete la cellule vie
           pour l'equilibrer : metrique par-planete + vitale (cf PlacesChip). -->
      <div class="ah-res-row ah-res-bottom">
        <ResourceChip v-for="r in groupMap.precieuses" :key="r.resourceId" :resource="r" group="precieuses" fluid />
        <span v-if="groupMap.precieuses.length && groupMap.vie.length" class="ah-res-sep" aria-hidden="true" />
        <ResourceChip v-for="r in groupMap.vie" :key="r.resourceId" :resource="r" group="vie" fluid />
        <PlacesChip
          v-if="resourceSummary.placesTotal > 0"
          :used="resourceSummary.placesUsed"
          :total="resourceSummary.placesTotal"
          fluid
        />
      </div>
    </template>

    <!-- Bouton mobile : ouvre un drawer plein ecran avec toutes les ressources
         (la barre desktop est cachee en CSS sous 768px). -->
    <AppTooltip v-if="session.inUniverse" content="Voir les ressources">
      <button
        type="button"
        class="app-header-mobile-trigger"
        @click="mobileResourcesOpen = true"
        aria-label="Voir les ressources"
      >
        <el-icon><Coin /></el-icon>
        <span class="text-mono">{{ formatNumber(resourceSummary.playerFlouz) }}</span>
      </button>
    </AppTooltip>

    <div class="app-header-right">
      <!-- Phase 2 refonte nav : icones globales (inbox, quetes, recherches,
           inventaire, chat) + dropdown "Plus". Cache sur mobile (relai
           MobileBottomNav). -->
      <HeaderGlobalNav />

      <el-dropdown trigger="click">
        <span class="app-header-user">
          <el-icon><Avatar /></el-icon>
          <span class="app-header-user-name">{{ auth.user?.login ?? '...' }}</span>
          <el-icon class="el-icon--right"><ArrowDown /></el-icon>
        </span>

        <template #dropdown>
          <el-dropdown-menu>
            <el-dropdown-item disabled>
              {{ auth.user?.email }}
            </el-dropdown-item>

            <el-dropdown-item v-if="session.inUniverse" divided @click="onLeaveUniverse">
              <el-icon><Back /></el-icon>
              Quitter l'univers
            </el-dropdown-item>
            <el-dropdown-item :divided="!session.inUniverse" @click="onLogout">
              <el-icon><SwitchButton /></el-icon>
              Déconnexion
            </el-dropdown-item>
          </el-dropdown-menu>
        </template>
      </el-dropdown>
    </div>

    <!-- Drawer mobile : panneau plein-largeur avec flouz + ressources par
         groupe. Affiche meme les ressources a zero (LEFT JOIN backend). -->
    <el-drawer
      v-model="mobileResourcesOpen"
      direction="rtl"
      size="86%"
      :with-header="true"
      title="Ressources"
      append-to-body
      class="app-header-mobile-drawer"
    >
      <div class="mobile-resources">
        <!-- Flouz en tete -->
        <section class="mobile-resources-section mobile-resources-flouz">
          <div class="mobile-resources-section-head">
            <span class="mobile-resources-section-title">Flouz</span>
            <span class="mobile-resources-section-sub text-muted text-xs">Solde du joueur</span>
          </div>
          <div class="mobile-resource-row">
            <img
              :src="flouzImgUrl"
              alt="Flouz"
              class="mobile-resource-icon"
              v-img-hide-on-error
            />
            <span class="mobile-resource-amount text-mono">
              {{ resourceSummary.playerFlouz.toLocaleString() }}
            </span>
          </div>
        </section>

        <!-- Sections planetaires : un bloc par categorie. -->
        <template v-if="resourceSummary.planetName">
          <div class="mobile-resources-planet text-muted text-xs">
            Planete : {{ resourceSummary.planetName }}
          </div>
          <section
            v-for="g in resourceGroups"
            :key="g.key"
            :class="['mobile-resources-section', `is-${g.key}`]"
          >
            <div class="mobile-resources-section-head">
              <span class="mobile-resources-section-title">{{ g.label }}</span>
            </div>
            <div
              v-for="r in g.resources"
              :key="r.resourceId"
              class="mobile-resource-row"
            >
              <img
                :src="`/images/thumb/64/resources/${r.code}.png`"
                :alt="r.label"
                class="mobile-resource-icon"
                v-img-hide-on-error
              />
              <div class="mobile-resource-meta">
                <div class="mobile-resource-name">{{ r.label }}</div>
                <div class="mobile-resource-rate text-muted text-xs">
                  <span :class="r.productionPerHour >= 0 ? 'is-pos' : 'is-neg'">
                    {{ r.productionPerHour >= 0 ? '+' : '' }}{{ formatNumber(r.productionPerHour) }}/h
                  </span>
                  <span v-if="r.stored > 0">
                    · {{ formatNumber(r.available) }} / {{ formatNumber(r.stored) }}
                  </span>
                </div>
              </div>
              <span class="mobile-resource-amount text-mono">{{ resourceHeadline(r) }}</span>
            </div>
          </section>
        </template>
      </div>
    </el-drawer>
  </header>
</template>

<style lang="scss" scoped>
.app-header {
  // Grille 2 lignes x 2 colonnes :
  //   col1 = identite (haut) / menu+login (bas)  |  col2 = ressources (etirees)
  //   1:2 = construction | carburants     2:2 = precieuses | special/vie
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  grid-template-areas:
    "identity restop"
    "nav      resbottom";
  align-items: center;
  column-gap: $space-3;
  row-gap: 4px;
  min-height: $layout-header-height;
  padding: $space-1 $space-4;
  background: $bg-primary;
  border-bottom: 1px solid $border-subtle;

  // Mobile : pas de grille ressources (drawer), simple barre brand/trigger/nav.
  @media (max-width: 767.99px) {
    display: flex;
    justify-content: space-between;
  }
}

// Placement des cellules dans la grille (l'ordre DOM n'importe pas).
.app-header-left  { grid-area: identity; }
.app-header-right { grid-area: nav; }
.ah-res-top    { grid-area: restop; }
.ah-res-bottom { grid-area: resbottom; }

// Ligne de ressources : les chips s'etirent (fluid) pour remplir la largeur,
// toutes egales entre elles, avec un separateur entre les 2 types.
.ah-res-row {
  display: flex;
  align-items: stretch;
  // nowrap : chaque ligne (matériaux|consommables, précieuses|vie) reste sur UNE
  // seule ligne, les chips rétrécissant (min-width:0) au lieu de wrapper. Garantit
  // une répartition identique sur les 2 lignes -> séparateurs alignés à la verticale.
  flex-wrap: nowrap;
  gap: 4px;
  min-width: 0;

  @media (max-width: 767.99px) { display: none; }
}

// Separateur vertical entre les deux groupes d'une ligne.
.ah-res-sep {
  flex: 0 0 auto;
  align-self: stretch;
  width: 1px;
  min-height: 22px;
  margin: 0 6px;
  background: $border-default;
}

// =====================================================================
// Bloc gauche : brand "The H'O" + contexte joueur (flouz + planete)
// Adaptatif :
//   - >= $bp-xl  : brand + contexte cote a cote, sur 1 seule ligne
//   - 768-1279px : empile (brand row 1, contexte row 2). Les chips
//                  ressources demarrent ainsi au meme X sur les 2 lignes
//                  quand ca wrap = colonnes alignees.
//   - < 768px    : brand cache (drawer ressources prend le relai).
// =====================================================================
.app-header-left {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 4px;
  flex-shrink: 0;

  @media (min-width: 1280px) {
    flex-direction: row;
    align-items: center;
    gap: $space-3;
  }
}

.app-header-brand {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  padding: 0 $space-3;
  text-decoration: none;
  color: $text-primary;
  transition: color $duration-fast $easing-default;

  &:hover {
    color: $color-neon-cyan;
  }

  @media (max-width: 767.99px) {
    display: none;
  }
}

.app-header-brand-text {
  font-family: $font-display;
  font-size: $font-size-lg;
  font-weight: $font-weight-bold;
  letter-spacing: 0.12em;
  text-shadow: 0 0 12px rgba($color-neon-cyan-bright, 0.4);
}

// Marqueur de version (build front servi). Petit, discret, à côté du brand.
.app-header-version {
  margin-left: $space-2;
  font-family: $font-mono;
  font-size: 10px;
  line-height: 1;
  color: $text-muted;
  opacity: 0.7;
  align-self: center;
}

// Flouz + nom de planete (contexte joueur) -- partage le bord gauche
// avec le brand pour que les chips ressources s'alignent verticalement.
.app-header-context {
  display: flex;
  align-items: center;
  gap: $space-2;
  padding: 0 $space-3;

  // Cache sur mobile : le drawer ressources (.app-header-mobile-trigger)
  // expose deja le solde flouz + le contexte planete.
  @media (max-width: 767.99px) {
    display: none;
  }
}

.app-header-context-planet {
  font-size: $font-size-xs;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  white-space: nowrap;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
}

// Nom de planete cliquable -> retour sur la page d'accueil de la planete.
.app-header-context-planet-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  text-decoration: none;
  color: $text-secondary;
  padding: 2px 6px;
  border-radius: $radius-sm;
  border: 1px solid transparent;
  transition: all $duration-fast $easing-default;
  cursor: pointer;

  .el-icon { font-size: 13px; flex-shrink: 0; }
  .app-header-context-planet-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 150px;
  }

  &:hover {
    color: $color-neon-cyan;
    border-color: rgba($color-neon-cyan, 0.4);
    background: rgba($color-neon-cyan, 0.06);
  }
}

.app-header-right {
  display: flex;
  align-items: center;
  gap: $space-2;
  flex-shrink: 0;
}

.app-header-user {
  display: inline-flex;
  align-items: center;
  gap: $space-2;
  cursor: pointer;
  color: $text-primary;
  transition: color $duration-fast $easing-default;

  &:hover {
    color: $color-neon-cyan;
  }
}

.app-header-user-name {
  font-weight: $font-weight-medium;
}

// =====================================================================
// Barre de ressources desktop (cachee en mobile)
// =====================================================================
// Wrap autorise -> 2 lignes possibles. Le header reste en min-height
// fixe ; quand la barre wrap, la hauteur s'agrandit via align-items
// du parent (.app-header definit gap + flex-start verticalement).
.app-header-resources {
  // Grille 2x2 des groupes de ressources (ordre = materiaux, consommables,
  // precieuses, vie) -> ligne 1 : construction | carburants ;
  // ligne 2 : precieuses | vie.
  // Grille 2x2 dont les 2 colonnes se PARTAGENT la largeur restante (1fr 1fr)
  // pour que la barre occupe tout l'espace dispo -> les chips peuvent grandir.
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: start;
  justify-content: stretch;
  gap: $space-1 $space-2;
  flex: 1 1 auto;
  min-width: 0;
  margin: 0 $space-2;
  padding: $space-1 $space-2;

  @media (max-width: 767.99px) {
    display: none;
  }
}

.app-header-resources-empty {
  font-size: $font-size-xs;
  font-style: italic;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

// Chip ressource : largeur FIXE pour que la grille reste homogene quels
// que soient les montants (un "1234" ne doit pas pousser ses voisins).
// La police du montant est calculee dans le script (amountFontSize) en
// fonction de la longueur du texte -- shrink de 12px a 9px max.
.app-header-resource {
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
  // Largeur + hauteur FIXES et IDENTIQUES pour TOUTES les chips, quel que soit
  // le groupe ou le nombre de ressources : on ne fait plus de shrink-to-fit (qui
  // donnait des chips de tailles differentes). Les groupes wrappent si besoin.
  flex: 0 0 auto;
  width: 112px;
  height: 26px;
  padding: 0 6px;
  background: rgba($color-neon-cyan-bright, $alpha-subtle);
  border: 1px solid rgba($color-neon-cyan-bright, $alpha-light);
  border-radius: $radius-sm;
  font-size: $font-size-xs;
  overflow: hidden;
}

.app-header-resource-amount {
  color: $text-primary;
  flex: 1 1 auto;
  text-align: right;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1;
}

// Fleche de tendance de production (▲ hausse / ▼ deficit / = stable).
// Lecture d'un coup d'oeil de l'evolution du stock par ressource.
.app-header-resource-trend {
  flex-shrink: 0;
  font-size: 9px;
  line-height: 1;
  margin-left: 1px;

  &.is-up { color: $color-success; }
  &.is-down { color: $color-danger; }
  &.is-stable { color: $text-muted; }
}

// Badge dedie pour le solde flouz du joueur (PlayerUniverse.Solde).
// Couleur warning + icone pour le distinguer des ressources cyan.
.app-header-flouz {
  // Meme taille fixe que les chips ressources (uniformite totale de la barre).
  flex: 0 0 auto;
  width: 112px;
  background: rgba($color-warning, 0.10);
  border-color: rgba($color-warning, 0.45);

  .app-header-resource-amount {
    color: $color-warning;
    font-weight: $font-weight-medium;
  }
}

// Badge ressource avec icone (image generee) : on remplace le code texte
// par un sprite carre. Le hover affiche un el-popover (ResourceTooltipContent).
.app-header-resource-img {
  cursor: help;
  padding: 2px $space-1 2px 2px;
  gap: 6px;

  &:hover {
    border-color: rgba($color-neon-cyan-bright, $alpha-medium);
  }
}

.app-header-resource-icon {
  width: 22px;
  height: 22px;
  object-fit: contain;
  display: block;
  flex-shrink: 0;
}

// Groupes par USAGE : 4 categories visuellement distinctes -- chaque
// groupe garde ses ressources cote a cote dans un wrapper teinte. Le
// wrapper lui-meme peut wrapper sur la 2e ligne du header.
.app-header-resource-group {
  display: inline-flex;
  // Chips a taille fixe identique -> le groupe wrappe sur plusieurs lignes si
  // sa cellule (1fr) ne suffit pas, plutot que de deformer les chips.
  flex-wrap: wrap;
  min-width: 0;
  align-items: center;
  gap: 4px;
  padding: 2px 6px;
  border-radius: $radius-sm;

  // Teinte la bordure des chips + fond du groupe selon l'usage :
  //   - Materiaux    -> ambre (mineraux bruts)
  //   - Consommables -> cyan (carburants / polymeres)
  //   - Precieuses   -> violet (gemmes)
  //   - Vie          -> vert (habitants / nourriture / energie)
  &.is-materiaux {
    background: rgba($color-warning, 0.04);
    .app-header-resource {
      background: rgba($color-warning, 0.10);
      border-color: rgba($color-warning, 0.35);
    }
  }
  &.is-consommables {
    background: rgba($color-neon-cyan-bright, 0.04);
  }
  &.is-precieuses {
    background: rgba($accent-legendary, 0.05);
    .app-header-resource {
      background: rgba($accent-legendary, 0.12);
      border-color: rgba($accent-legendary, 0.40);
    }
  }
  &.is-vie {
    background: rgba($color-success, 0.05);
    .app-header-resource {
      background: rgba($color-success, 0.12);
      border-color: rgba($color-success, 0.40);
    }
  }
}

// =====================================================================
// Mobile trigger button (icone qui ouvre le drawer ressources)
// =====================================================================
.app-header-mobile-trigger {
  display: none;
  align-items: center;
  gap: $space-1;
  padding: $space-1 $space-3;
  background: rgba($color-warning, 0.10);
  border: 1px solid rgba($color-warning, 0.35);
  border-radius: $radius-sm;
  color: $color-warning;
  font-size: $font-size-sm;
  cursor: pointer;
  transition: all $duration-fast $easing-default;

  &:hover {
    background: rgba($color-warning, 0.18);
  }

  @media (max-width: 767.99px) {
    display: inline-flex;
    margin-left: auto;
  }
}

// =====================================================================
// Popover flouz
// =====================================================================
.flouz-tip {
  display: flex;
  flex-direction: column;
  gap: $space-2;
}
.flouz-tip-head {
  display: flex;
  align-items: center;
  gap: $space-2;
  padding-bottom: $space-2;
  border-bottom: 1px solid $border-default;
}
.flouz-tip-icon {
  width: 32px;
  height: 32px;
  object-fit: contain;
}
.flouz-tip-name {
  font-weight: $font-weight-medium;
  color: $color-warning;
}
.flouz-tip-sub {
  margin: 0;
}
.flouz-tip-amount {
  font-size: $font-size-lg;
  color: $text-primary;
  text-align: right;
}

// Lignes Portefeuille / Banque / Total dans le tooltip flouz.
.flouz-tip-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: $space-3;
  font-size: $font-size-sm;
}

.flouz-tip-label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: $text-secondary;
}

.flouz-tip-val {
  color: $text-primary;
  font-weight: $font-weight-medium;
}

.flouz-tip-bank .flouz-tip-label {
  color: $text-muted;
  font-style: italic;
}

.flouz-tip-total {
  margin-top: 2px;
  padding-top: $space-2;
  border-top: 1px solid $border-subtle;
  font-weight: $font-weight-medium;

  .flouz-tip-val { color: $color-warning; }
}

.flouz-tip-sep {
  height: 1px;
  background: $border-default;
  margin: $space-2 0 $space-1;
}

.flouz-tip-section {
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

// Bloc pret -- une carte par pret actif.
.flouz-tip-loan {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: $space-2;
  background: rgba($color-danger, 0.06);
  border: 1px solid rgba($color-danger, 0.30);
  border-radius: $radius-sm;
}

.flouz-tip-loan-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 2px;
}

.flouz-tip-loan-name {
  color: $text-primary;
  font-weight: $font-weight-medium;
}

.flouz-tip-loan-rate {
  color: $color-danger;
}

.flouz-tip-loan-row {
  display: flex;
  justify-content: space-between;
  font-size: $font-size-xs;
  color: $text-secondary;
}

.flouz-tip-loan-warn .text-mono { color: $color-warning; }
.flouz-tip-loan-paid .text-mono { color: $color-success; }

// =====================================================================
// Drawer mobile (ressources plein ecran)
// =====================================================================
.app-header-mobile-drawer {
  :deep(.el-drawer__body) {
    padding: $space-3;
  }
}

.mobile-resources {
  display: flex;
  flex-direction: column;
  gap: $space-4;
}

.mobile-resources-section {
  display: flex;
  flex-direction: column;
  gap: $space-2;
  padding: $space-3;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid $border-subtle;
  border-radius: $radius-md;

  // Aligne sur les memes 4 groupes que la barre desktop.
  &.is-materiaux {
    border-color: rgba($color-warning, 0.35);
  }
  &.is-consommables {
    border-color: rgba($color-neon-cyan-bright, $alpha-light + 0.05);
  }
  &.is-precieuses {
    border-color: rgba($accent-legendary, 0.40);
  }
  &.is-vie {
    border-color: rgba($color-success, 0.40);
  }
}

.mobile-resources-flouz {
  border-color: rgba($color-warning, 0.55);
  background: rgba($color-warning, 0.05);
}

.mobile-resources-section-head {
  display: flex;
  align-items: baseline;
  gap: $space-2;
  margin-bottom: $space-1;
}

.mobile-resources-section-title {
  font-family: $font-display;
  font-size: $font-size-md;
  font-weight: $font-weight-bold;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.mobile-resources-section-sub {
  margin: 0;
}

.mobile-resources-planet {
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding-left: $space-2;
}

.mobile-resource-row {
  display: flex;
  align-items: center;
  gap: $space-3;
  padding: $space-2 $space-1;

  & + & {
    border-top: 1px solid $border-subtle;
  }
}

.mobile-resource-icon {
  width: 28px;
  height: 28px;
  object-fit: contain;
  flex-shrink: 0;
}

.mobile-resource-meta {
  flex: 1 1 auto;
  min-width: 0;
}

.mobile-resource-name {
  font-size: $font-size-sm;
  color: $text-primary;
}

.mobile-resource-rate {
  margin-top: 2px;
  display: flex;
  gap: $space-1;
  align-items: baseline;

  .is-pos { color: $color-success; }
  .is-neg { color: $color-danger; }
}

.mobile-resource-amount {
  color: $text-primary;
  font-size: $font-size-md;
  flex-shrink: 0;
}
</style>
